import { useEffect } from 'react';
import { useApp } from '../context';
import { musicPlaying } from '../musicPlayer';
import type { PlexItem } from '../plex';
import { usePrefs } from '../prefs';

/**
 * Theme-muziek op de titelpagina, zoals Plex zelf: het themanummer van een serie (door Plex gehost)
 * of de theme.mp3 naast een film, zacht ingefaded en weer weg als je de pagina verlaat.
 * Wordt gestreamd van je Plex-server; er wordt niets gedownload of bewaard.
 */
export function ThemeMusic({ item }: { item: PlexItem }) {
  const { plex } = useApp();
  const prefs = usePrefs();
  const mode = prefs.themeMusic ?? 'off';
  const isShow = item.type === 'show' || item.type === 'season' || item.type === 'episode';
  const path = item.theme ?? item.parentTheme ?? item.grandparentTheme;
  const on = mode !== 'off' && !!path && (isShow || mode === 'all');
  const volume = Math.max(0, Math.min(1, (prefs.themeVolume ?? 35) / 100));

  useEffect(() => {
    if (!on || !path || musicPlaying()) return;
    const a = new Audio(plex.url(path));
    a.loop = false;
    a.volume = 0;
    let fade: number | undefined;
    const ramp = (to: number, ms: number, then?: () => void) => {
      window.clearInterval(fade);
      const from = a.volume;
      const t0 = performance.now();
      fade = window.setInterval(() => {
        const k = Math.min(1, (performance.now() - t0) / ms);
        a.volume = from + (to - from) * k;
        if (k >= 1) { window.clearInterval(fade); then?.(); }
      }, 50);
    };
    // Even wachten: bij snel doorklikken geen geluid
    const start = window.setTimeout(() => {
      void a.play().then(() => ramp(volume, 1500)).catch(() => undefined);
    }, 700);
    // Na het nummer zacht stoppen (sommige themes zijn lang)
    const stopAt = window.setTimeout(() => ramp(0, 2500, () => a.pause()), 60_000);
    return () => {
      window.clearTimeout(start);
      window.clearTimeout(stopAt);
      ramp(0, 600, () => { a.pause(); a.src = ''; });
    };
  }, [on, path, plex, volume]);

  return null;
}
